"use client";

import {
  TextField, Button, Typography, Box, Paper, Grid, Divider,
  Chip, InputAdornment, Avatar, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { getSweetAlert } from "../../helper";
import DepositDialog from "./DepositDialog";

export default function Deposit() {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const [searchId, setSearchId] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userKyc, setUserKyc] = useState({});
  const [open, setOpen] = useState(false);

  const [promoOpen, setPromoOpen] = useState(false);
  const [promoUser, setPromoUser] = useState(null);
  const [promoAmount, setPromoAmount] = useState("");
  const [promoRemarks, setPromoRemarks] = useState("");
  const [promoSubmitting, setPromoSubmitting] = useState(false);

  const handleSearch = async () => {
    const trimmed = searchId.trim();
    if (!trimmed) return;
    let param;
    if (/^\d{10}$/.test(trimmed)) param = `phone=${trimmed}`;
    else if (/^[a-zA-Z\s]+$/.test(trimmed)) param = `name=${encodeURIComponent(trimmed)}`;
    else param = `playerId=${trimmed}`;
    const res = await axiosRequest("GET", `/user_details?${param}`, token);
    setUsers(res?.data?.userDetailsList || (res?.data?.userDetails ? [res.data.userDetails] : []));
    setUserKyc(res?.data?.kycDetails || null);
  };

  const handleOpenDeposit = (user) => { setSelectedUser(user); setOpen(true); };

  const handleOpenPromo = (user) => { setPromoUser(user); setPromoAmount(""); setPromoRemarks(""); setPromoOpen(true); };

  const handleSubmitPromo = async () => {
    if (!promoAmount || Number(promoAmount) <= 0) { getSweetAlert("error", "Enter a valid amount"); return; }
    setPromoSubmitting(true);
    const res = await axiosRequest("POST", "/promo-credit", token, {
      user_id: promoUser._id,
      amount: Number(promoAmount),
      remarks: promoRemarks.trim(),
    });
    setPromoSubmitting(false);
    if (res?.data?.status !== false && res?.status !== "failure") {
      setPromoOpen(false);
      getSweetAlert("success", res?.message || res?.data?.message || "Promo credit added");
      handleSearch();
    } else {
      getSweetAlert("error", res?.data?.message || res?.message || "Failed to add promo credit");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>

      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>Deposit Management</Typography>
        <Typography color="text.secondary">Search a player and create a deposit entry</Typography>
      </Box>

      {/* Search */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid #e0e0e0" }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Find Player</Typography>
        <Box display="flex" gap={2}>
          <TextField
            fullWidth size="small"
            placeholder="Enter Player ID, 10-digit mobile, or player name"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
            }}
          />
          <Button variant="contained" onClick={handleSearch} sx={{ px: 3, borderRadius: 2, whiteSpace: "nowrap" }}>
            Search
          </Button>
        </Box>
      </Paper>

      {/* Search Result */}
      {users.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Search Result</Typography>
          {users.map((user) => (
            <Box
              key={user._id}
              display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}
              sx={{ p: 2, borderRadius: 2, background: "#1c2433", border: "1px solid #e8eaf6" }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: "#3f51b5", width: 48, height: 48 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography fontWeight={700} fontSize={16}>{user.name || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">ID: {user.playerId}</Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Winning Balance</Typography>
                  <Typography fontWeight={700} color="success.main">{Number(user.winning_balance || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Promo Balance</Typography>
                  <Typography fontWeight={700} color="secondary.main">{Number(user.promotional_balance || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Total Deposited</Typography>
                  <Typography fontWeight={700} color="primary.main">{Number(user.cash_deposite || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Loan Outstanding</Typography>
                  <Typography fontWeight={700} color={user.loan_amount > 0 ? "error.main" : "text.secondary"}>{Number(user.loan_amount || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">Float Outstanding</Typography>
                  <Typography fontWeight={700} color={Number(user.float_outstanding || 0) > 0 ? "warning.dark" : "text.secondary"}>{Number(user.float_outstanding || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                <Button variant="contained" onClick={() => handleOpenDeposit(user)} sx={{ borderRadius: 2, fontWeight: 600 }}>
                  + Deposit
                </Button>
                <Button variant="contained" color="secondary" onClick={() => handleOpenPromo(user)} sx={{ borderRadius: 2, fontWeight: 600 }}>
                  + Promo
                </Button>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {/* KYC */}
      {userKyc && users.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>KYC Details</Typography>
          <Grid container spacing={2} mb={2}>
            {[
              { label: "Aadhaar", verified: userKyc?.is_aadhar_verified },
              { label: "PAN", verified: userKyc?.is_pan_verified },
              { label: "Bank", verified: userKyc?.is_bank_verified },
            ].map(({ label, verified }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Box display="flex" alignItems="center" gap={1} sx={{ p: 1.5, borderRadius: 2, background: verified ? "#f0fdf4" : "#fff5f5", border: `1px solid ${verified ? "#bbf7d0" : "#fecaca"}` }}>
                  {verified ? <VerifiedIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                  <Box>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight={600} color={verified ? "success.main" : "error.main"}>
                      {verified ? "Verified" : "Not Verified"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Aadhaar Name</Typography><Typography fontWeight={600}>{userKyc?.aadhar_details?.name || "—"}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">PAN Name</Typography><Typography fontWeight={600}>{userKyc?.pen_details?.pan_name || "—"}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Aadhaar Number</Typography><Typography fontWeight={600}>{userKyc?.aadhar_details?.uid?.replace(/.(?=.{4})/g, "X") || "—"}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">PAN Number</Typography><Typography fontWeight={600}>{userKyc?.pen_details?.pan_card || "—"}</Typography></Grid>
          </Grid>
        </Paper>
      )}

      {/* Promo Credit Dialog */}
      <Dialog open={promoOpen} onClose={() => setPromoOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: "linear-gradient(135deg,#6a1b9a,#ab47bc)", color: "#fff", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <CardGiftcardIcon /> Add Promo Credit
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {promoUser && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: "#241530", border: "1px solid #ce93d8" }}>
              <Typography fontWeight={700}>{promoUser.name}</Typography>
              <Typography variant="caption" color="text.secondary">ID: {promoUser.playerId} &nbsp;|&nbsp; Promo: {Number(promoUser.promotional_balance || 0).toLocaleString("en-IN")}</Typography>
            </Box>
          )}
          <TextField
            fullWidth size="small" type="number" label="Amount ()"
            value={promoAmount} onChange={(e) => setPromoAmount(e.target.value)}
            inputProps={{ min: 1 }} sx={{ mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Remarks"
            placeholder="Reason for promo credit"
            value={promoRemarks} onChange={(e) => setPromoRemarks(e.target.value)}
            multiline rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPromoOpen(false)} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            onClick={handleSubmitPromo} variant="contained" color="secondary"
            disabled={promoSubmitting || !promoAmount}
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 140 }}
          >
            {promoSubmitting ? "Adding…" : "Add Promo Credit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deposit Dialog */}
      <DepositDialog
        open={open}
        onClose={() => { setOpen(false); setSelectedUser(null); }}
        initialUser={selectedUser}
        token={token}
        onSuccess={handleSearch}
      />

    </Box>
  );
}
